import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { DataQualityReport } from '@/lib/types';
import { CheckCircle, Info, Warning, XCircle } from '@phosphor-icons/react';

interface QualityReportProps {
  report: DataQualityReport;
  datasetName: string;
}

export function QualityReport({ report, datasetName }: QualityReportProps) {
  const getScoreColor = (score: number) => {
    if (score >= 90) {
      return 'text-quality-good';
    }
    if (score >= 70) {
      return 'text-quality-warning';
    }

    return 'text-quality-poor';
  };

  const getScoreIcon = (score: number) => {
    if (score >= 90) {
      return <CheckCircle className='w-5 h-5 text-quality-good' />;
    }
    if (score >= 70) {
      return <Warning className='w-5 h-5 text-quality-warning' />;
    }

    return <XCircle className='w-5 h-5 text-quality-poor' />;
  };

  const getIssueIcon = (type: string) => {
    switch (type) {
      case 'missing':
        return <Warning className='w-4 h-4 text-quality-warning' />;
      case 'invalid':
        return <XCircle className='w-4 h-4 text-quality-poor' />;
      case 'duplicate':
        return <Info className='w-4 h-4 text-blue-500' />;
      case 'outlier':
        return <Warning className='w-4 h-4 text-quality-warning' />;
      default:
        return <Info className='w-4 h-4' />;
    }
  };

  const qualityMetrics = [
    {
      name: 'Completeness',
      score: report.completeness,
      description: 'Percentage of non-missing values',
    },
    {
      name: 'Validity',
      score: report.validity,
      description: 'Percentage of valid data formats',
    },
    {
      name: 'Consistency',
      score: report.consistency,
      description: 'Data follows expected patterns',
    },
    {
      name: 'Uniqueness',
      score: report.uniqueness,
      description: 'Percentage of unique records',
    },
  ];

  const overallScore =
    (report.completeness +
      report.validity +
      report.consistency +
      report.uniqueness) /
    4;

  return (
    <div className='space-y-6'>
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            {getScoreIcon(overallScore)}
            Data Quality Report - {datasetName}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6'>
            {qualityMetrics.map(metric => (
              <div key={metric.name} className='space-y-2'>
                <div className='flex items-center justify-between'>
                  <span className='text-sm font-medium'>{metric.name}</span>
                  <span
                    className={`text-sm font-bold ${getScoreColor(metric.score)}`}
                  >
                    {metric.score.toFixed(1)}%
                  </span>
                </div>
                <Progress value={metric.score} className='h-2' />
                <p className='text-xs text-muted-foreground leading-tight'>
                  {metric.description}
                </p>
              </div>
            ))}
          </div>

          <div className='p-4 bg-muted/50 rounded-lg border'>
            <div className='flex items-center gap-2 mb-2'>
              {getScoreIcon(overallScore)}
              <span className='font-semibold'>Overall Quality Score</span>
              <Badge className={`ml-auto ${getScoreColor(overallScore)}`}>
                {overallScore.toFixed(1)}%
              </Badge>
            </div>
            <p className='text-sm text-muted-foreground'>
              {overallScore >= 90
                ? 'Excellent data quality - ready for analysis!'
                : overallScore >= 70
                  ? 'Good data quality with minor issues to address.'
                  : 'Data quality needs improvement before analysis.'}
            </p>
          </div>
        </CardContent>
      </Card>

      {report.issues.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className='text-lg'>Data Quality Issues</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='space-y-3'>
              {report.issues.map((issue, index) => (
                <div
                  key={index}
                  className='flex items-start gap-3 p-3 border rounded-lg hover:shadow-sm transition-shadow'
                >
                  <div className='flex-shrink-0 mt-0.5'>
                    {getIssueIcon(issue.type)}
                  </div>
                  <div className='flex-1 min-w-0'>
                    <div className='flex items-center gap-2 mb-1'>
                      <span className='font-medium capitalize text-sm'>
                        {issue.type} Values
                      </span>
                      <Badge variant='outline' className='text-xs font-mono'>
                        {issue.field}
                      </Badge>
                    </div>
                    <p className='text-sm text-muted-foreground mb-2 leading-tight'>
                      {issue.description}
                    </p>
                    <div className='flex items-center gap-4 text-xs'>
                      <span>
                        Count: <strong>{issue.count}</strong>
                      </span>
                      <span>
                        Percentage:{' '}
                        <strong>{issue.percentage.toFixed(1)}%</strong>
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {report.recommendations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className='text-lg'>Recommendations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className='space-y-2'>
              {report.recommendations.map((recommendation, index) => (
                <div key={index} className='flex items-start gap-2'>
                  <CheckCircle className='w-4 h-4 text-quality-good mt-0.5 flex-shrink-0' />
                  <span className='text-sm'>{recommendation}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
